import { observable, action } from 'mobx';

import {
  Section,
  UserProfile,
  CustomMarkerDefinition,
  CustomMarkerSection,
  practitionerSectionKey,
  renameDefinitionsSection,
  renameInDefaultTestSections,
} from '../models';
import { MarkersOutsideOptimalDisplay } from '../models/UserProfile';
import { UnitSystem } from '../models/units/unitConversions';
import { LoadingStore } from './loading-store';
import { UserProfileStore } from './user-profile-store';

export class UserProfileUIStore {
  @observable currentUserProfile: UserProfile;
  @observable logoUpdateTrigger: number = 0; // Trigger for logo updates

  constructor(
    private userProfileStore: UserProfileStore,
    private loadingStore: LoadingStore,
  ) {}

  @action loadCurrentUserProfile = async (): Promise<UserProfile> => {
    this.loadingStore.appIsBusy();

    this.currentUserProfile =
      await this.userProfileStore.loadCurrentUserProfile();

    this.loadingStore.appIsNotBusy();

    return this.currentUserProfile;
  };

  @action saveCurrentUserProfile = async (): Promise<UserProfile> => {
    this.loadingStore.appIsBusy();

    // finally, not a trailing call: a rejected save used to skip appIsNotBusy()
    // and leave the spinner modal up over a locked page, which is the worst
    // possible way to report a failure — it looks like the app hung rather than
    // like something needs fixing.
    try {
      this.currentUserProfile = await this.userProfileStore.updateUserProfile(
        this.currentUserProfile,
      );
      return this.currentUserProfile;
    } finally {
      this.loadingStore.appIsNotBusy();
    }
  };

  @action updateLogo = (logoUrl: string): void => {
    if (this.currentUserProfile) {
      this.currentUserProfile.customLogoUrl = logoUrl;
      this.logoUpdateTrigger = Date.now(); // Trigger re-render
    }
  };

  @action removeLogo = (): void => {
    if (this.currentUserProfile) {
      this.currentUserProfile.customLogoUrl = '';
      this.logoUpdateTrigger = Date.now(); // Trigger re-render
    }
  };

  @action updatePreparedBy = (value: string): void => {
    if (this.currentUserProfile) {
      this.currentUserProfile.preparedBy = value;
    }
  };

  /**
   * The cover-page heading on each PDF, edited in Report Settings.
   *
   * Stored trimmed and stored EMPTY rather than deleted when it is cleared: an
   * empty string resolves to the shipped default the same way a missing field
   * does (see models/reportTitles), and leaving the key in place means the PUT
   * carries the practitioner's clearing to Dynamo instead of the field simply
   * going missing from the body and the old title surviving the save.
   */
  @action updateReportTitles = (
    analysisTitle: string,
    comparisonTitle: string,
  ): void => {
    if (this.currentUserProfile) {
      this.currentUserProfile.analysisReportTitle = (analysisTitle || '').trim();
      this.currentUserProfile.comparisonReportTitle = (
        comparisonTitle || ''
      ).trim();
    }
  };

  /**
   * The practitioner's own name, edited on the Profile page.
   *
   * This writes the profile, not the Cognito user. The two can diverge: the
   * name Cognito holds is whatever was typed at sign-up, and nothing writes
   * back to it. That is deliberate for now, because the pool's app client may
   * not have given_name/family_name marked writable (they are only populated in
   * prod, see UserProfile), and a save that throws for half the practitioners
   * is worse than one the app fully owns. Navigation reads the profile first
   * and falls back to the session token, so an edit here shows up immediately.
   */
  @action updateName = (firstName: string, lastName: string): void => {
    if (this.currentUserProfile) {
      this.currentUserProfile.firstName = firstName;
      this.currentUserProfile.lastName = lastName;
    }
  };

  @action updateEnabledSupplementBrands = (brands: string[]): void => {
    if (this.currentUserProfile) {
      this.currentUserProfile.enabledSupplementBrands = brands;
    }
  };

  @action updateExcludedFromPdfSections = (sections: Section[]): void => {
    if (this.currentUserProfile) {
      this.currentUserProfile.excludedFromPdfSections = sections;
    }
  };

  @action updateMarkersOutsideOptimalDisplay = (mode: MarkersOutsideOptimalDisplay): void => {
    if (this.currentUserProfile) {
      this.currentUserProfile.markersOutsideOptimalDisplay = mode;
    }
  };

  @action updateShowOnlySectionSignals = (value: boolean): void => {
    if (this.currentUserProfile) {
      this.currentUserProfile.showOnlySectionSignals = value;
    }
  };

  @action updateShowOnlyImmuneSectionSignals = (value: boolean): void => {
    if (this.currentUserProfile) {
      this.currentUserProfile.showOnlyImmuneSectionSignals = value;
    }
  };

  @action updateShowImmuneAbsolutes = (value: boolean): void => {
    if (this.currentUserProfile) {
      this.currentUserProfile.showImmuneAbsolutes = value;
    }
  };

  @action updateShowPreviousTestMarkersInComparison = (value: boolean): void => {
    if (this.currentUserProfile) {
      this.currentUserProfile.showPreviousTestMarkersInComparison = value;
    }
  };

  @action updateUnitSystem = (value: UnitSystem): void => {
    if (this.currentUserProfile) {
      this.currentUserProfile.unitSystem = value;
    }
  };

  @action updateCustomMarkerLibrary = (
    library: CustomMarkerDefinition[],
  ): void => {
    if (this.currentUserProfile) {
      this.currentUserProfile.customMarkerLibrary = library;
    }
  };

  @action updateCustomMarkerSections = (
    sections: CustomMarkerSection[],
  ): void => {
    if (this.currentUserProfile) {
      this.currentUserProfile.customMarkerSections = sections;
    }
  };

  @action updateDefaultTestSections = (sections: string[]): void => {
    if (this.currentUserProfile) {
      this.currentUserProfile.defaultTestSections = sections;
    }
  };

  @action updateDefaultTestMarkerIds = (markerIds: string[]): void => {
    if (this.currentUserProfile) {
      this.currentUserProfile.defaultTestMarkerIds = markerIds;
    }
  };

  @action updateExpandedTestSections = (sections: string[]): void => {
    if (this.currentUserProfile) {
      this.currentUserProfile.expandedTestSections = sections;
    }
  };

  /**
   * Add a practitioner-named report section and persist it.
   *
   * Called from the Section dropdown wherever a custom marker is filed, so a
   * section created mid-entry is on the list for the next client. A duplicate
   * name is a no-op rather than an error: the dropdown already refuses one, and
   * two clicks on a slow connection must not produce two sections.
   */
  @action addCustomMarkerSection = (name: string, id: string): Promise<any> => {
    const profile = this.currentUserProfile;
    const trimmed = (name || '').trim();
    if (!profile || !trimmed) {
      return Promise.resolve();
    }
    const existing = profile.customMarkerSections || [];
    if (
      existing.some(
        section =>
          practitionerSectionKey(section.name) === practitionerSectionKey(trimmed),
      )
    ) {
      return Promise.resolve();
    }
    profile.customMarkerSections = [
      ...existing,
      new CustomMarkerSection({ id, name: trimmed }),
    ];
    return this.saveCurrentUserProfile();
  };

  /**
   * Rename a section in the practitioner's list, matched on its current name.
   *
   * Only the list. Markers store the name they were saved with, so a report
   * already issued to a client keeps the section heading it was issued under —
   * the caller renames the rows on the test in front of them, and older tests
   * keep theirs until they are opened. Adds the section if it is not on the
   * list yet, which is what happens when the typo was never saved there.
   */
  @action renameCustomMarkerSection = (
    from: string,
    to: string,
    newId: string,
  ): Promise<any> => {
    const profile = this.currentUserProfile;
    const trimmed = (to || '').trim();
    if (!profile || !trimmed) {
      return Promise.resolve();
    }
    const fromKey = practitionerSectionKey(from);
    const existing = profile.customMarkerSections || [];
    const match = existing.find(
      section => practitionerSectionKey(section.name) === fromKey,
    );
    if (!match) {
      return this.addCustomMarkerSection(trimmed, newId);
    }
    profile.customMarkerSections = existing.map(section =>
      section === match
        ? new CustomMarkerSection({ id: section.id, name: trimmed })
        : section,
    );
    // The library's definitions carry the name too, and the My Markers page
    // renames them when a section is renamed there. Skipping them here left
    // the list saying one spelling and the definitions another, so a section
    // added to the NEXT test arrived filed under a name the list no longer
    // has — its Section dropdown fell back to Other Lab Results while its
    // card, which groups case-insensitively, still read the section's name.
    profile.customMarkerLibrary = renameDefinitionsSection(
      profile.customMarkerLibrary,
      from,
      trimmed,
    );
    // BOTH per-section settings store the NAME, so a rename that skipped either
    // would quietly drop it: the panel would stop loading onto new tests, or
    // its markers would start folding again on Add Test Results. The My Markers
    // page handles the pair together; this path is the other way a section gets
    // renamed (from the Section dropdown while entering a test).
    profile.defaultTestSections = renameInDefaultTestSections(
      profile.defaultTestSections,
      from,
      trimmed,
    );
    profile.expandedTestSections = renameInDefaultTestSections(
      profile.expandedTestSections,
      from,
      trimmed,
    );
    return this.saveCurrentUserProfile();
  };
}
